import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const baseRoute = "back/api/multiplesample";
const historyRoute = "back/api/multiplesample/history";

const _multiplesampleHistorySchema = z.object({
  id: z.string(),
  exemplaireDansEstampesMultiplesHistoryDescriptionNom: z.string(),
  exemplaireDansEstampesMultiplesHistoryDescriptionVille: z.string(),
  exemplaireDansEstampesMultiplesHistoryDescriptionDates: z.string(),
  exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart:
    z.boolean(),
  multiplesampleId: z.string().nullable(),
  updatedAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedById: z.string().optional().nullable(),
  createdById: z.string().optional().nullable(),
  provenanceRank: z.number().optional(),
});

export type MultiplesampleHistory = z.infer<
  typeof _multiplesampleHistorySchema
>;

export type MultiplesampleHistoryMergedAuction = {
  kind: "auction";
  id: string;
  multiplesampleId: string | null;
  instanceId: string;
  auctionId: string | null;
  provenanceRank?: number;
  auction: {
    id: string;
    ventesDescriptionNumeroVente: string;
    ventesDescriptionTitre: string;
    ventesDescriptionDate: string;
    ventesDescriptionLieu: string;
    ventesDescriptionVille: string;
    ventesDescriptionPays: string;
  } | null;
  multiplesampleDansVentesDescriptionNumeroLot: string;
  multiplesampleDansVentesDescriptionNotes: string;
  createdAt?: string;
  updatedAt?: string | null;
};

export type MultiplesampleHistoryMerged = {
  timeline: Array<
    (MultiplesampleHistory & { kind: "history" }) |
      MultiplesampleHistoryMergedAuction
  >;
};

async function getHistoryOfExemplaire(exemplaireId: string) {
  const { data } = await api.get<MultiplesampleHistory[]>(
    `${baseRoute}/${exemplaireId}/history`,
  );
  return data;
}

async function getHistoryOfExemplaireMerged(exemplaireId: string) {
  const { data } = await api.get<MultiplesampleHistoryMerged>(
    `${baseRoute}/${exemplaireId}/history`,
    {
      params: { includeAuctions: "true" },
    },
  );
  return data;
}

export function useGetHistoryOfExemplaire(exemplaireId: string | undefined) {
  return useQuery({
    queryKey: ["multiplesample", exemplaireId, "history"],
    queryFn: () =>
      exemplaireId
        ? getHistoryOfExemplaire(exemplaireId)
        : Promise.resolve([]),
    enabled: !!exemplaireId,
  });
}

export function useGetHistoryOfExemplaireMerged(
  exemplaireId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["multiplesample", exemplaireId, "history", "withAuctions"],
    queryFn: () =>
      exemplaireId
        ? getHistoryOfExemplaireMerged(exemplaireId)
        : Promise.resolve({ timeline: [] } as MultiplesampleHistoryMerged),
    enabled: !!exemplaireId && enabled,
  });
}

const _createMultiplesampleHistorySchema = _multiplesampleHistorySchema.omit({
  id: true,
  multiplesampleId: true,
  createdAt: true,
  createdById: true,
  updatedAt: true,
  updatedById: true,
});

export type CreateMultiplesampleHistory = z.infer<
  typeof _createMultiplesampleHistorySchema
>;

async function createMultiplesampleHistory({
  history,
  exemplaireId,
}: {
  history: CreateMultiplesampleHistory;
  exemplaireId: string;
}) {
  const { data } = await api.post(
    `${baseRoute}/${exemplaireId}/history`,
    history,
  );
  return data;
}

export function useCreateMultiplesampleHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMultiplesampleHistory,
    onSuccess: (_, { exemplaireId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multiplesample", exemplaireId, "history"],
      });
    },
  });
}

export type EditMultiplesampleHistory = CreateMultiplesampleHistory;

async function editMultiplesampleHistory({
  history,
  historyId,
}: {
  history: EditMultiplesampleHistory;
  historyId: string;
  exemplaireId: string;
}) {
  const { data } = await api.patch(`${historyRoute}/${historyId}`, history);
  return data;
}

export function useEditMultiplesampleHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editMultiplesampleHistory,
    onSuccess: (_, { exemplaireId, historyId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multiplesample-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["multiplesample", exemplaireId, "history"],
      });
    },
  });
}

async function deleteMultiplesampleHistory({
  historyId,
}: {
  historyId: string;
  exemplaireId: string;
}) {
  await api.delete(`${historyRoute}/${historyId}`);
}

export function useDeleteMultiplesampleHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMultiplesampleHistory,
    onSuccess: (_, { exemplaireId, historyId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multiplesample-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["multiplesample", exemplaireId, "history"],
      });
    },
  });
}

async function updateMultiplesampleHistorySequence({
  newSequence,
  exemplaireId,
}: {
  newSequence: string[];
  exemplaireId: string;
}) {
  await api.patch(`${baseRoute}/${exemplaireId}/history`, {
    sequence: newSequence,
  });
}

export function useUpdateMultiplesampleHistorySequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMultiplesampleHistorySequence,
    onSuccess: (_, { exemplaireId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multiplesample", exemplaireId, "history"],
      });
    },
  });
}

async function reorderProvenanceTimeline({
  exemplaireId,
  timeline,
}: {
  exemplaireId: string;
  timeline: { kind: "history" | "auction"; id: string }[];
}) {
  await api.patch(`${baseRoute}/${exemplaireId}/provenance-sequence`, {
    timeline,
  });
}

export function useReorderMultiplesampleProvenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderProvenanceTimeline,
    onSuccess: (_, { exemplaireId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multiplesample", exemplaireId, "history"],
      });
    },
  });
}
